import React, { PropsWithChildren, StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import '../node_modules/@schuettflix/planum-react/dist/style.css';
import { ToasterContextProvider } from '@/shared/context/ToasterContext.tsx';
import { BrowserRouter, useNavigate } from 'react-router';
import { AuthProvider } from 'react-oidc-context';
import { Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { createOidcConfig } from '@/tools/auth/oidcConfig.ts';
import { OKTA_ISSUER } from '@/shared/utils/decodeJwt.ts';
import { environment } from '@/environment.ts';
import { initializeSentry } from '@/tools/sentry/initializeSentry.ts';
import { PlanumI18nProvider } from '@schuettflix/planum-react';
import { useTranslation } from 'react-i18next';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useQueryErrorHandler } from '@/shared/hooks/useQueryErrorHandler.ts';
import { App } from '@/App.tsx';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthTokenContextProvider } from '@/shared/context/AuthTokenContext.tsx';
import { initializeUserflow } from '@/tools/userflow.ts';

// Built only on the Okta path — the discriminated-union env exposes OKTA_CLIENT_ID there.
const oktaAuth =
    environment.AUTH_PROVIDER === 'okta'
        ? new OktaAuth({
              issuer: OKTA_ISSUER,
              clientId: environment.OKTA_CLIENT_ID,
              redirectUri: window.location.origin + '/login/callback',
              scopes: ['openid', 'profile', 'email', 'groups'],
          })
        : null;

// Built only on the OAuth path — the discriminated-union env exposes the OAuth vars there.
const oidcConfig =
    environment.AUTH_PROVIDER === 'oauth'
        ? createOidcConfig(environment.OAUTH_ISSUER, environment.OAUTH_CLIENT_ID)
        : null;

initializeSentry();
initializeUserflow();

function CoreProviders({ children }: PropsWithChildren) {
    const { i18n } = useTranslation();
    const { handleCacheErrors } = useQueryErrorHandler();

    const queryClient = useMemo(
        () =>
            new QueryClient({
                queryCache: new QueryCache({
                    ...handleCacheErrors,
                }),
            }),
        [handleCacheErrors]
    );

    return (
        <PlanumI18nProvider locale={i18n.language}>
            <QueryClientProvider client={queryClient}>
                <AuthTokenContextProvider>{children}</AuthTokenContextProvider>
            </QueryClientProvider>
        </PlanumI18nProvider>
    );
}

function AuthenticationProvider({ children }: PropsWithChildren) {
    const navigate = useNavigate();
    const restoreOriginalUri = useMemo(
        () => (_oktaAuth: OktaAuth, originalUri: string) => {
            void navigate(toRelativeUrl(originalUri || '/', window.location.origin));
        },
        [navigate]
    );

    if (environment.AUTH_PROVIDER === 'oauth') {
        return <AuthProvider {...oidcConfig!}>{children}</AuthProvider>;
    }

    return (
        <Security oktaAuth={oktaAuth!} restoreOriginalUri={restoreOriginalUri}>
            {children}
        </Security>
    );
}

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <BrowserRouter>
            <AuthenticationProvider>
                <ToasterContextProvider>
                    <CoreProviders>
                        <App />
                        <ReactQueryDevtools />
                    </CoreProviders>
                </ToasterContextProvider>
            </AuthenticationProvider>
        </BrowserRouter>
    </StrictMode>
);
